import React from 'react'
import dayjs from 'dayjs'
import { Button } from '@mobi/component-library/Base/Buttons'
import { ContextMenu } from '@mobi/component-library/Overlays/ContextMenu'
import { trackEvent } from '@core/Services/Analytics/track'
import type { NotificationTime } from '../../types'

export const TimePickerPopover: React.FC<{
  selectedTimes: Set<NotificationTime>
  validNotificationOptions: readonly NotificationTime[]
  setSelectedTimes: (setter: (prev: Set<NotificationTime>) => Set<NotificationTime>) => void
  registerAlert: (time: Set<NotificationTime>) => void
  deregisterAlert: (time: Set<NotificationTime>) => void
  contextMenuRef: React.RefObject<HTMLDivElement | null>
  analyticsDataRef: React.RefObject<RaceAlertAnalyticsData | null>
  isTimeSelectMenuOpen: boolean
  setIsTimeSelectMenuOpen: (value: React.SetStateAction<boolean>) => void
}> = ({
  selectedTimes,
  setSelectedTimes,
  registerAlert,
  deregisterAlert,
  validNotificationOptions,
  contextMenuRef,
  analyticsDataRef,
  isTimeSelectMenuOpen,
  setIsTimeSelectMenuOpen,
}) => {
  const initialSelectedTimesRef = React.useRef(selectedTimes)

  React.useEffect(() => {
    const data = analyticsDataRef
    const initialSelectedTimes = initialSelectedTimesRef.current
    return () => {
      if (!data.current || areSelectedTimesEqual(initialSelectedTimes, data.current.selectedTimes))
        return
      trackAnalytics({ ...data.current })
    }
  }, [analyticsDataRef])

  return (
    <ContextMenu
      isOpen={isTimeSelectMenuOpen}
      referenceElRef={contextMenuRef}
      isAutoPlacement
      handleDismiss={() => setIsTimeSelectMenuOpen(false)}
      headingText='Set your reminder(s)'
    >
      {validNotificationOptions.map(time => (
        <Button
          key={time}
          size='xs'
          color={selectedTimes.has(time) ? 'primary' : 'secondary_outline'}
          onClick={e => {
            e.stopPropagation()
            setSelectedTimes(prev => {
              const updatedTimes = new Set(prev)

              if (updatedTimes.has(time)) {
                updatedTimes.delete(time)
              } else {
                updatedTimes.add(time)
              }

              if (updatedTimes.size === 0) {
                deregisterAlert(updatedTimes)
              } else {
                registerAlert(updatedTimes)
              }

              return updatedTimes
            })
          }}
        >
          {time} min
        </Button>
      ))}
    </ContextMenu>
  )
}

// =============
// Local Helpers
// =============

// Analytics tracking

const serialisedSet = (set: Set<unknown>) => [...set].join('')

function areSelectedTimesEqual(
  initialSelectedTimes: Set<NotificationTime>,
  newSelectedTimes: Set<NotificationTime>
) {
  return serialisedSet(initialSelectedTimes) === serialisedSet(newSelectedTimes)
}

const trackAnalytics = ({
  selectedTimes,
  ast,
  isHeaderPinned,
  eventId,
}: RaceAlertAnalyticsData) => {
  const timeToJump = dayjs(ast).diff(dayjs(), 'second')

  if (selectedTimes.size === 0)
    trackRaceNotificationDisabled({
      timeToJump,
      eventId,
    })
  else
    trackRaceNotificationEnabled({
      timeToJump,
      eventId,
      headerState: isHeaderPinned ? 'sticky' : 'default',
      ...selectedTimesToDictionary(selectedTimes ?? new Set()),
    })
}

function selectedTimesToDictionary(selectedTimes: Set<NotificationTime>) {
  const allTimes: NotificationTime[] = [1, 5, 10, 15]
  return allTimes.reduce(
    (result, n) => {
      result[`alert${n}`] = selectedTimes.has(n)
      return result
    },
    {} as Record<`alert${NotificationTime}`, boolean>
  )
}

const trackRaceNotificationEnabled = ({ headerState, ...data }: AnalyticsDataEnabled): void => {
  trackEvent('race-notification-enabled', { data, headerState })
}

const trackRaceNotificationDisabled = (data: AnalyticsData): void => {
  trackEvent('race-notification-disabled', { data })
}

// =====
// Types
// =====

type AnalyticsData = {
  eventId: string
  timeToJump: number
}

type AnalyticsDataEnabled = AnalyticsData & {
  headerState?: 'sticky' | 'default'
} & Record<`alert${NotificationTime}`, boolean>

export type RaceAlertAnalyticsData = {
  selectedTimes: Set<NotificationTime>
  eventId: string
  ast: string
  isHeaderPinned?: boolean
}
